/**
 * Machine ID Auth Integration
 * 
 * Integrates machine ID attestation with Clerk authentication
 */

import { runAttestationFlow, shouldTriggerAttestation } from './machineIdAttestationFlow';
import * as Sentry from '@sentry/react';
import { authState } from '../stores/shared';

/**
 * Initialize machine ID attestation after Clerk authentication
 * 
 * This should be called when:
 * - User successfully logs in with Clerk
 * - App initializes and user is already authenticated
 * - User switches accounts
 * 
 * @param clerkUserId - Clerk user ID
 */
export async function initializeMachineIdAttestation(
  clerkUserId: string,
): Promise<void> {
  try {
    // Check if we should trigger attestation
    if (!shouldTriggerAttestation(clerkUserId)) {
      console.log('[MachineIdAuthIntegration] Attestation not needed at this time');
      return;
    }

    console.log('[MachineIdAuthIntegration] Initializing machine ID attestation');

    // Get the current Clerk token from authState (auto-populated)
    const token = authState.currentState.accessToken;
    
    if (!token) {
      console.error('[MachineIdAuthIntegration] No Clerk token available');
      Sentry.captureMessage('Machine ID attestation skipped - no Clerk token', {
        level: 'warning',
        tags: {
          event_type: 'machine_id_attestation_no_token',
        },
        contexts: {
          auth: {
            clerkUserId,
          },
        },
      });
      return;
    }

    // Run the attestation flow
    await runAttestationFlow(clerkUserId, token);
  } catch (error) {
    console.error('[MachineIdAuthIntegration] Error initializing machine ID attestation:', error);
    Sentry.captureException(error, {
      tags: {
        event_type: 'machine_id_attestation_init_error',
      },
      contexts: {
        auth: {
          clerkUserId,
        },
      },
    });
  }
}

/**
 * Handle Clerk session update
 * 
 * Call this when the Clerk session changes (login, logout, user switch)
 * 
 * @param clerkUserId - Clerk user ID (undefined if logged out)
 */
export async function handleClerkSessionUpdate(
  clerkUserId: string | undefined,
): Promise<void> {
  if (!clerkUserId) {
    console.log('[MachineIdAuthIntegration] User logged out, skipping attestation');
    return;
  }

  // Initialize attestation for the current user
  await initializeMachineIdAttestation(clerkUserId);
}

